import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useGetEventUsers } from "../../api/queries/useGetEventUsers";
import { useGetEvent } from "../../api/queries/useGetEvent";
import { useGetConnections } from "../../api/queries/useGetConnections";
import { useGetConnectionRequests } from "../../api/queries/useGetConnectionRequests";
import { useGetSentConnectionRequests } from "../../api/queries/useGetSentConnectionsRequests";
import { useAppContext } from "../../Context/AppContext";
import { explorerOnboardingQueryKey } from "../../auth/authSession";
import { getMyExplorerOnboarding } from "../../api/explorerOnboardingApi";
import { isLoggedIn } from "../../auth/authStorage";
import { User } from "../../types/Types";
import type { AttendeeWithState } from "./EventAttendeeCard";
import { useTranslation } from "react-i18next";
import { useLiveTranslatedInput } from "../../hooks/useLiveTranslatedInput";
import { useExplorerConnectionLimit } from "../../hooks/useExplorerConnectionLimit";
import { useExplorerConnectionLimitFeedback } from "../../hooks/useExplorerConnectionLimitFeedback";
import {
  resolveCurrentUserConnectionSource,
  resolvedObjectId,
} from "../../utils/connectionUserUtils";
import { useGetMyAttendeeNotes } from "../../api/queries/useGetMyAttendeeNotes";
import {
  loadHighlightedAttendeeKeys,
  toggleHighlightedAttendeeKey,
} from "../../utils/attendeeHighlightState";
import {
  EventUserRecord,
  getEventAttendeeObjectId,
  mergeAttendeeDisplayUser,
  PAGE_SIZE,
  sameConnectionUser,
  userFromEventAttendeeRecord,
} from "./eventAttendeesHelpers";
import { useEventAttendeeConnections } from "./useEventAttendeeConnections";

export function useEventAttendees() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { objectId, user: currentUser } = useAppContext();
  const userId = objectId ?? "";

  const {
    canPerformConnectionAction,
    refetchLimit: refetchExplorerConnectionLimit,
    isExplorer,
    used,
    limit,
    weekResetsUtc,
  } = useExplorerConnectionLimit();
  const explorerLimitReached = !canPerformConnectionAction;
  const {
    guardConnectionAction,
    handleConnectionLimitError,
    limitModal,
  } = useExplorerConnectionLimitFeedback({ autoShowPaywall: true });

  const { data: explorerMe } = useQuery({
    queryKey: explorerOnboardingQueryKey(objectId),
    queryFn: getMyExplorerOnboarding,
    enabled: isLoggedIn() && !!objectId && isExplorer,
    retry: false,
    staleTime: 60_000,
  });

  const currentConnectionSource = useMemo(
    () =>
      resolveCurrentUserConnectionSource(
        currentUser?.user,
        explorerMe,
        isExplorer
      ),
    [currentUser?.user, explorerMe, isExplorer]
  );

  const [searchTerm, setSearchTerm] = useState("");
  const {
    bindLiveText: bindAttendeeSearch,
    retranslateCurrent: retranslateAttendeeSearch,
    messageTargetLang: attendeeSearchTargetLang,
  } = useLiveTranslatedInput();
  const searchTermRef = useRef(searchTerm);
  searchTermRef.current = searchTerm;
  const [sortOption, setSortOption] = useState("connections.sort.recent");
  const [showMobileSort, setShowMobileSort] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [noteUser, setNoteUser] = useState<User | null>(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedAttendeeKeys, setSelectedAttendeeKeys] = useState<Set<string>>(
    () => new Set()
  );

  const { data: eventUsersData, isLoading: loadingAttendees } = useGetEventUsers({
    id: eventId || "",
    enabled: !!eventId,
  });
  const { data: eventData } = useGetEvent({
    eventId: eventId || "",
    enabled: !!eventId,
  });
  const { data: myAttendeeNotes } = useGetMyAttendeeNotes(!!eventId);
  const { data: connections, refetch: refetchConnections } = useGetConnections({
    userId,
  });
  const { data: connectionRequests, refetch: refetchConnectionRequests } =
    useGetConnectionRequests({ userId });
  const { data: sentConnectionRequests, refetch: refetchSentConnectionRequests } =
    useGetSentConnectionRequests({ userId });

  const connectionsApi = useEventAttendeeConnections({
    t,
    locale,
    userId,
    objectId: objectId ?? undefined,
    currentUserId: currentUser?.user?.id ?? 0,
    currentUserName: `${currentUser?.user?.firstName ?? ""} ${currentUser?.user?.lastName ?? ""}`.trim(),
    weekResetsUtc: weekResetsUtc ?? undefined,
    used,
    limit,
    guardConnectionAction,
    handleConnectionLimitError,
    refetchExplorerConnectionLimit,
    refetchConnections,
    refetchConnectionRequests,
    refetchSentConnectionRequests,
  });

  const noteObjectIdsForEvent = useMemo(() => {
    const eid = Number(eventId);
    const set = new Set<string>();
    if (!eid || !myAttendeeNotes) return set;
    for (const n of myAttendeeNotes) {
      if (n.eventId === eid && n.attendeeObjectId) {
        set.add(n.attendeeObjectId.trim().toLowerCase());
      }
    }
    return set;
  }, [myAttendeeNotes, eventId]);

  const connectionUsers = useMemo(
    () => connections?.userDtos ?? [],
    [connections?.userDtos]
  );
  const sentRequestUsers = useMemo(
    () => sentConnectionRequests?.userDtos ?? [],
    [sentConnectionRequests?.userDtos]
  );
  const receivedRequestUsers = useMemo(
    () => connectionRequests?.userDtos ?? [],
    [connectionRequests?.userDtos]
  );

  const attendeesList = useMemo(() => {
    const rows = (eventUsersData?.users as EventUserRecord[] | undefined) ?? [];
    return rows
      .filter((u) => getEventAttendeeObjectId(u))
      .map((eu, idx) => userFromEventAttendeeRecord(eu, idx));
  }, [eventUsersData?.users]);

  const attendeesWithState = useMemo((): AttendeeWithState[] => {
    return attendeesList.map((user) => {
      let connectionState: AttendeeWithState["connectionState"] = "none";
      let actionUser = user;

      const matchedConnection = connectionUsers.find((candidate) =>
        sameConnectionUser(user, candidate)
      );
      const matchedSentRequest = sentRequestUsers.find((candidate) =>
        sameConnectionUser(user, candidate)
      );
      const matchedReceivedRequest = receivedRequestUsers.find((candidate) =>
        sameConnectionUser(user, candidate)
      );

      if (matchedConnection) {
        connectionState = "connected";
        actionUser = matchedConnection;
      } else if (matchedSentRequest) {
        connectionState = "sent";
        actionUser = matchedSentRequest;
      } else if (matchedReceivedRequest) {
        connectionState = "received";
        actionUser = matchedReceivedRequest;
      }

      const matchedCurrentUser =
        currentUser?.user && sameConnectionUser(user, currentUser.user)
          ? currentUser.user
          : null;

      return {
        user: mergeAttendeeDisplayUser(
          user,
          matchedConnection ??
            matchedSentRequest ??
            matchedReceivedRequest ??
            matchedCurrentUser
        ),
        actionUser,
        connectionState,
      };
    });
  }, [
    attendeesList,
    connectionUsers,
    sentRequestUsers,
    receivedRequestUsers,
    currentUser?.user,
  ]);

  const viewerOidNorm = useMemo(() => {
    const fromCtx = (objectId ?? "").toString().trim().toLowerCase();
    const fromProf = resolvedObjectId(currentUser?.user).toLowerCase();
    return fromCtx || fromProf;
  }, [objectId, currentUser?.user]);

  const viewerOidDuplicateCount = useMemo(() => {
    if (!viewerOidNorm) return 0;
    return attendeesList.filter(
      (u) => resolvedObjectId(u).toLowerCase() === viewerOidNorm
    ).length;
  }, [attendeesList, viewerOidNorm]);

  const filteredAndSorted = useMemo(() => {
    let list = [...attendeesWithState];
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      list = list.filter((a) => {
        const first = (a.user.firstName || "").toLowerCase();
        const last = (a.user.lastName || "").toLowerCase();
        return (
          first.includes(term) ||
          last.includes(term) ||
          `${first} ${last}`.trim().includes(term)
        );
      });
    }
    if (sortOption === "connections.sort.firstName")
      list.sort((a, b) =>
        (a.user.firstName || "").trim().localeCompare((b.user.firstName || "").trim())
      );
    else if (sortOption === "connections.sort.lastName")
      list.sort((a, b) =>
        (a.user.lastName || "").trim().localeCompare((b.user.lastName || "").trim())
      );
    return list;
  }, [attendeesWithState, searchTerm, sortOption]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSorted.length / PAGE_SIZE));
  const paginatedList = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredAndSorted.slice(start, start + PAGE_SIZE);
  }, [filteredAndSorted, currentPage]);

  const showPagination = filteredAndSorted.length > PAGE_SIZE;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortOption]);

  useEffect(() => {
    if (!userId || !eventId) {
      setSelectedAttendeeKeys(new Set());
      return;
    }
    setSelectedAttendeeKeys(loadHighlightedAttendeeKeys(userId, eventId));
  }, [userId, eventId]);

  useEffect(() => {
    retranslateAttendeeSearch(() => searchTermRef.current, setSearchTerm);
  }, [attendeeSearchTargetLang, retranslateAttendeeSearch]);

  const handleSaveNote = useCallback((user: User) => {
    setNoteUser(user);
    setShowNoteModal(true);
  }, []);

  const handleToggleAttendeeSelect = useCallback(
    (key: string) => {
      if (!userId || !eventId) return;
      setSelectedAttendeeKeys(toggleHighlightedAttendeeKey(userId, eventId, key));
    },
    [userId, eventId]
  );

  const handleMessage = useCallback(
    (id: number) => {
      if (isExplorer) {
        const isConnected = connections?.userDtos?.some((u) => u.id === id);
        if (!isConnected) return;
      }
      navigate(`/messages/${id}`);
    },
    [navigate, isExplorer, connections?.userDtos]
  );

  return {
    eventId,
    navigate,
    currentUser: currentUser?.user,
    isExplorer,
    explorerLimitReached,
    currentConnectionSource,
    limitModal,
    searchTerm,
    setSearchTerm,
    bindAttendeeSearch,
    attendeeSearchTargetLang,
    sortOption,
    setSortOption,
    showMobileSort,
    setShowMobileSort,
    currentPage,
    setCurrentPage,
    noteUser,
    showNoteModal,
    setShowNoteModal,
    setNoteUser,
    selectedAttendeeKeys,
    loadingAttendees,
    eventData,
    noteObjectIdsForEvent,
    attendeesWithState,
    viewerOidNorm,
    viewerOidDuplicateCount,
    filteredAndSorted,
    totalPages,
    paginatedList,
    showPagination,
    handleSaveNote,
    handleToggleAttendeeSelect,
    handleMessage,
    guardConnectionAction,
    ...connectionsApi,
  };
}
